// Gambar generation utilities using fal.ai API

// Generate image using fal.ai API with retry mechanism
export const generateImage = async (prompt, aspectRatio = '1:1', retryCount = 0) => {
  const maxRetries = 3
  
  try {
    console.log(`🔄 Mengirim request ke fal.ai API... (attempt ${retryCount + 1}/${maxRetries + 1})`)
    
    const requestBody = {
      prompt: prompt,
      aspect_ratio: aspectRatio,
      num_inference_steps: 4,
      num_images: 1,
      enable_safety_checker: true
    }
    
    console.log('📝 Request body:', requestBody)
    
    const apiKey = import.meta.env.VITE_FAL_KEY
    
    if (!apiKey) {
      throw new Error('FAL API Key tidak tersedia. Silakan set VITE_FAL_KEY di environment variable.')
    }
    
    // Create AbortController for timeout
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout
    
    const response = await fetch('https://fal.run/fal-ai/imagen4/preview/fast', {
      method: 'POST',
      headers: {
        'Authorization': `Key ${apiKey}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody),
      signal: controller.signal
    })
    
    clearTimeout(timeoutId)
    
    if (!response.ok) {
      const errorText = await response.text()
      console.error('❌ API Error Response:', errorText)
      throw new Error(`API Error: ${response.status} - ${errorText}`)
    }
    
    const result = await response.json()
    console.log('✅ API Response:', result)
    
    if (!result.images || result.images.length === 0) {
      throw new Error('Tidak ada gambar yang dihasilkan dari API')
    }
    
    const imageData = {
      url: result.images[0].url,
      width: result.images[0].width,
      height: result.images[0].height,
      prompt: prompt,
      aspectRatio: aspectRatio,
      generatedAt: new Date().toISOString(),
      seed: result.seed || null
    }
    
    console.log('🎨 Image generated successfully:', imageData)
    return imageData
    
  } catch (error) {
    console.error(`❌ Error generating image (attempt ${retryCount + 1}):`, error)
    
    // Handle AbortError (timeout)
    if (error.name === 'AbortError') {
      if (retryCount < maxRetries) {
        console.log(`⏰ Request timeout, retrying... (${retryCount + 1}/${maxRetries})`)
        await new Promise(resolve => setTimeout(resolve, 2000)) // Wait 2 seconds before retry
        return generateImage(prompt, aspectRatio, retryCount + 1)
      } else {
        throw new Error('Request timeout setelah beberapa percobaan. Server terlalu lama merespons.')
      }
    }
    
    // Handle retryable errors
    const isRetryableError = 
      error.message.includes('500') || 
      error.message.includes('502') || 
      error.message.includes('503') || 
      error.message.includes('504') ||
      error.message.includes('NetworkError') ||
      error.message.includes('fetch') ||
      error.name === 'TypeError'
    
    if (isRetryableError && retryCount < maxRetries) {
      console.log(`🔄 Retryable error, attempting retry... (${retryCount + 1}/${maxRetries})`)
      await new Promise(resolve => setTimeout(resolve, (retryCount + 1) * 2000)) // Exponential backoff
      return generateImage(prompt, aspectRatio, retryCount + 1)
    }
    
    // Enhanced error handling with more specific messages
    if (error.message.includes('401') || error.message.includes('Unauthorized')) {
      throw new Error('API Key tidak valid atau expired. Silakan hubungi administrator.')
    } else if (error.message.includes('429') || error.message.includes('Too Many Requests')) {
      throw new Error('Terlalu banyak request. Silakan tunggu 30 detik dan coba lagi.')
    } else if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
      throw new Error('Server fal.ai sedang bermasalah. Silakan coba lagi dalam 5 menit.')
    } else if (error.message.includes('503') || error.message.includes('Service Unavailable')) {
      throw new Error('Layanan fal.ai sedang maintenance. Silakan coba lagi nanti.')
    } else if (error.message.includes('NetworkError') || error.message.includes('fetch') || error.name === 'TypeError') {
      throw new Error('Koneksi internet bermasalah atau server tidak dapat dijangkau. Periksa koneksi Anda.')
    } else if (error.message.includes('timeout')) {
      throw new Error('Request timeout. Server terlalu lama merespons, silakan coba lagi.')
    } else if (!error.message || error.message === '') {
      throw new Error('Terjadi kesalahan tidak dikenal saat membuat gambar. Silakan coba lagi.')
    } else {
      throw new Error(`Gagal membuat gambar: ${error.message}`)
    }
  }
}

// Get available aspect ratios (supported by Imagen 4)
export const getAvailableAspectRatios = () => {
  return [
    { value: '1:1', label: 'Square (1:1)', description: 'Persegi' },
    { value: '16:9', label: 'Landscape (16:9)', description: 'Lanskap lebar' },
    { value: '9:16', label: 'Portrait (9:16)', description: 'Potret tinggi' },
    { value: '3:4', label: 'Portrait (3:4)', description: 'Standar potret' },
    { value: '4:3', label: 'Standard (4:3)', description: 'Standar lanskap' }
  ]
}

// Download image function
export const downloadImage = async (imageUrl, filename = 'generated-image') => {
  try {
    const response = await fetch(imageUrl)
    const blob = await response.blob()
    
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${filename}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    
    console.log('✅ Image downloaded successfully')
  } catch (error) {
    console.error('❌ Error downloading image:', error)
    throw new Error('Gagal mengunduh gambar')
  }
}